import { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Plus,
  Filter,
  RefreshCw,
  Wrench,
  Calendar,
  DollarSign,
  Pencil,
  Trash2,
  X,
  ChevronDown,
  Settings,
  Hammer,
  ClipboardCheck,
  Scale,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/app/components/ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/app/components/ui/collapsible';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/app/components/ui/pagination';
import { toast } from '@/app/components/ui/simple-toaster';
import {
  useMaintenance,
  useCreateMaintenance,
  useUpdateMaintenance,
  useDeleteMaintenance,
  type MaintenanceRecordWithDetails,
} from '@/hooks/use-maintenance';
import { useAssets } from '@/hooks/use-assets';
import { useOrgCurrency, useOrgToday } from '@/hooks/use-settings';
import { formatMoney } from '@/lib/currency';
import { formatDateOnly } from '@/lib/date-utils';
import { usePermissions } from '@/hooks/use-permissions';
import type { MaintenanceType, CreateMaintenancePayload } from '@/lib/types';

const typeColors: Record<MaintenanceType, string> = {
  service: 'bg-blue-100 text-blue-800',
  repair: 'bg-orange-100 text-orange-800',
  inspection: 'bg-green-100 text-green-800',
  load_test: 'bg-purple-100 text-purple-800',
};

const typeIcons: Record<MaintenanceType, React.ReactNode> = {
  service: <Settings className="h-4 w-4" />,
  repair: <Hammer className="h-4 w-4" />,
  inspection: <ClipboardCheck className="h-4 w-4" />,
  load_test: <Scale className="h-4 w-4" />,
};

const typeLabels: Record<MaintenanceType, string> = {
  service: 'Service',
  repair: 'Repair',
  inspection: 'Inspection',
  load_test: 'Load Test',
};

const PAGE_SIZE = 20;

interface MaintenanceFormData {
  asset: number | '';
  date: string;
  type: MaintenanceType;
  cost: string;
  notes: string;
}

const getInitialFormData = (orgToday: string): MaintenanceFormData => ({
  asset: '',
  date: orgToday,
  type: 'service',
  cost: '',
  notes: '',
});

export function MaintenancePage() {
  const orgCurrency = useOrgCurrency();
  const orgToday = useOrgToday();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<MaintenanceRecordWithDetails | null>(null);
  const [formData, setFormData] = useState<MaintenanceFormData>(() => getInitialFormData(orgToday));
  const [dateError, setDateError] = useState<string | null>(null);

  const page = parseInt(searchParams.get('page') || '1', 10);
  const typeFilter = (searchParams.get('type') || '') as MaintenanceType | '';
  const assetId = searchParams.get('asset') || '';
  const dateAfter = searchParams.get('date_after') || '';
  const dateBefore = searchParams.get('date_before') || '';

  const { data, isLoading, isError, refetch } = useMaintenance({
    page,
    page_size: PAGE_SIZE,
    type: typeFilter,
    asset: assetId ? parseInt(assetId, 10) : '',
    date_after: dateAfter,
    date_before: dateBefore,
  });

  const { data: assetsData } = useAssets({ page_size: 100 });
  const { canCreateMaintenance } = usePermissions();
  const createMutation = useCreateMaintenance();
  const updateMutation = useUpdateMaintenance();
  const deleteMutation = useDeleteMaintenance();

  const records = data?.results || [];
  const totalCount = data?.count || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const updateSearchParams = useCallback(
    (updates: Record<string, string>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      if (!updates.page) {
        newParams.set('page', '1');
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const clearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  const validateDate = (dateStr: string): boolean => {
    if (dateStr > orgToday) {
      setDateError('Date cannot be in the future');
      return false;
    }
    setDateError(null);
    return true;
  };

  const openCreateDialog = () => {
    setFormData(getInitialFormData(orgToday));
    setDateError(null);
    setIsCreateDialogOpen(true);
  };

  const openEditDialog = (record: MaintenanceRecordWithDetails) => {
    setSelectedRecord(record);
    setFormData({
      asset: record.asset,
      date: record.date,
      type: record.type,
      cost: record.cost || '',
      notes: record.notes || '',
    });
    setDateError(null);
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (record: MaintenanceRecordWithDetails) => {
    setSelectedRecord(record);
    setIsDeleteDialogOpen(true);
  };

  const handleCreate = async () => {
    if (!formData.asset) {
      toast.error('Please select an asset');
      return;
    }
    if (!formData.date) {
      toast.error('Date is required');
      return;
    }
    if (!validateDate(formData.date)) {
      return;
    }

    try {
      const payload: CreateMaintenancePayload = {
        asset: formData.asset as number,
        date: formData.date,
        type: formData.type,
      };
      if (formData.cost) {
        payload.cost = formData.cost;
      }
      if (formData.notes.trim()) {
        payload.notes = formData.notes.trim();
      }

      await createMutation.mutateAsync(payload);
      toast.success('Maintenance record created successfully');
      setIsCreateDialogOpen(false);
      setFormData(getInitialFormData(orgToday));
    } catch (_error) {
      toast.error('Failed to create maintenance record');
    }
  };

  const handleUpdate = async () => {
    if (!selectedRecord) return;
    if (!formData.asset) {
      toast.error('Please select an asset');
      return;
    }
    if (!formData.date) {
      toast.error('Date is required');
      return;
    }
    if (!validateDate(formData.date)) {
      return;
    }

    try {
      await updateMutation.mutateAsync({
        id: selectedRecord.id,
        data: {
          asset: formData.asset as number,
          date: formData.date,
          type: formData.type,
          cost: formData.cost || undefined,
          notes: formData.notes.trim() || undefined,
        },
      });
      toast.success('Maintenance record updated successfully');
      setIsEditDialogOpen(false);
      setSelectedRecord(null);
      setFormData(getInitialFormData(orgToday));
    } catch (_error) {
      toast.error('Failed to update maintenance record');
    }
  };

  const handleDelete = async () => {
    if (!selectedRecord) return;

    try {
      await deleteMutation.mutateAsync(selectedRecord.id);
      toast.success('Maintenance record deleted successfully');
      setIsDeleteDialogOpen(false);
      setSelectedRecord(null);
    } catch (_error) {
      toast.error('Failed to delete maintenance record');
    }
  };

  const hasActiveFilters = typeFilter || assetId || dateAfter || dateBefore;

  const formatDate = (dateString: string) => {
    return formatDateOnly(dateString, '—', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCost = (cost: string | null) => {
    if (!cost) return '-';
    return formatMoney(cost, orgCurrency);
  };

  const getAssetName = (assetIdNum: number) => {
    const asset = assetsData?.results?.find((a) => a.id === assetIdNum);
    return asset ? asset.display_name : `Asset #${assetIdNum}`;
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Maintenance Records</h1>
          <p className="text-gray-600 mt-1">
            Track equipment service, repairs, and load tests
            {totalCount > 0 && ` (${totalCount} total)`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {canCreateMaintenance && (
            <Button onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Add Record
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Settings className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Service</p>
                <p className="text-2xl font-bold">
                  {records.filter((r) => r.type === 'service').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Hammer className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Repairs</p>
                <p className="text-2xl font-bold">
                  {records.filter((r) => r.type === 'repair').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <ClipboardCheck className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Inspections</p>
                <p className="text-2xl font-bold">
                  {records.filter((r) => r.type === 'inspection').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Scale className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Load Tests</p>
                <p className="text-2xl font-bold">
                  {records.filter((r) => r.type === 'load_test').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Select
              value={typeFilter || 'all'}
              onValueChange={(value) => updateSearchParams({ type: value === 'all' ? '' : value })}
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="service">Service</SelectItem>
                <SelectItem value="repair">Repair</SelectItem>
                <SelectItem value="inspection">Inspection</SelectItem>
                <SelectItem value="load_test">Load Test</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={assetId || 'all'}
              onValueChange={(value) => updateSearchParams({ asset: value === 'all' ? '' : value })}
            >
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="All Assets" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Assets</SelectItem>
                {assetsData?.results?.map((asset) => (
                  <SelectItem key={asset.id} value={String(asset.id)}>
                    {asset.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Collapsible open={showFilters} onOpenChange={setShowFilters}>
              <CollapsibleTrigger asChild>
                <Button variant="outline">
                  <Filter className="h-4 w-4 mr-2" />
                  Date Range
                  <ChevronDown
                    className={`h-4 w-4 ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`}
                  />
                </Button>
              </CollapsibleTrigger>
            </Collapsible>
            {hasActiveFilters && (
              <Button variant="ghost" onClick={clearFilters} className="text-sm">
                <X className="h-4 w-4 mr-2" />
                Clear filters
              </Button>
            )}
          </div>

          <Collapsible open={showFilters} onOpenChange={setShowFilters}>
            <CollapsibleContent className="space-y-4 pt-4 border-t">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date From</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="date"
                      className="pl-10"
                      value={dateAfter}
                      onChange={(e) => updateSearchParams({ date_after: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Date To</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="date"
                      className="pl-10"
                      value={dateBefore}
                      onChange={(e) => updateSearchParams({ date_before: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : isError ? (
            <div className="p-6 text-center text-red-600">
              Failed to load maintenance records. Please try again.
            </div>
          ) : records.length === 0 ? (
            <div className="p-12 text-center">
              <Wrench className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No maintenance records found.</p>
              {hasActiveFilters && (
                <Button variant="link" onClick={clearFilters} className="mt-2">
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Asset</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Cost</TableHead>
                  <TableHead>Performed By</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell>
                      <span className="font-medium">{formatDate(record.date)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-600">
                        {record.asset_name || getAssetName(record.asset)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${typeColors[record.type]} flex items-center gap-1 w-fit`}>
                        {typeIcons[record.type]}
                        {typeLabels[record.type]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-600">{formatCost(record.cost)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-600">{record.performed_by_username || '-'}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-600 truncate max-w-xs block">
                        {record.notes || '-'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {canCreateMaintenance && (
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(record)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeleteDialog(record)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => updateSearchParams({ page: String(Math.max(1, page - 1)) })}
                className={page <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }
              return (
                <PaginationItem key={pageNum}>
                  <PaginationLink
                    onClick={() => updateSearchParams({ page: String(pageNum) })}
                    isActive={page === pageNum}
                    className="cursor-pointer"
                  >
                    {pageNum}
                  </PaginationLink>
                </PaginationItem>
              );
            })}
            <PaginationItem>
              <PaginationNext
                onClick={() => updateSearchParams({ page: String(Math.min(totalPages, page + 1)) })}
                className={page >= totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Maintenance Record</DialogTitle>
            <DialogDescription>
              Create a new maintenance record for an asset.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="asset">Asset *</Label>
              <Select
                value={formData.asset ? String(formData.asset) : ''}
                onValueChange={(value) => setFormData({ ...formData, asset: parseInt(value, 10) })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an asset" />
                </SelectTrigger>
                <SelectContent>
                  {assetsData?.results?.map((asset) => (
                    <SelectItem key={asset.id} value={String(asset.id)}>
                      {asset.display_name} ({asset.serial_number})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="date">Date *</Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                max={orgToday}
                onChange={(e) => {
                  setFormData({ ...formData, date: e.target.value });
                  validateDate(e.target.value);
                }}
              />
              {dateError && <p className="text-sm text-red-600">{dateError}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="type">Type *</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData({ ...formData, type: value as MaintenanceType })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="service">Service</SelectItem>
                  <SelectItem value="repair">Repair</SelectItem>
                  <SelectItem value="inspection">Inspection</SelectItem>
                  <SelectItem value="load_test">Load Test</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cost">Cost</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="cost"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  className="pl-10"
                  value={formData.cost}
                  onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Enter notes (optional)"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create Record'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Maintenance Record</DialogTitle>
            <DialogDescription>
              Update the maintenance record details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-asset">Asset *</Label>
              <Select
                value={formData.asset ? String(formData.asset) : ''}
                onValueChange={(value) => setFormData({ ...formData, asset: parseInt(value, 10) })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an asset" />
                </SelectTrigger>
                <SelectContent>
                  {assetsData?.results?.map((asset) => (
                    <SelectItem key={asset.id} value={String(asset.id)}>
                      {asset.display_name} ({asset.serial_number})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-date">Date *</Label>
              <Input
                id="edit-date"
                type="date"
                value={formData.date}
                max={orgToday}
                onChange={(e) => {
                  setFormData({ ...formData, date: e.target.value });
                  validateDate(e.target.value);
                }}
              />
              {dateError && <p className="text-sm text-red-600">{dateError}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-type">Type *</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData({ ...formData, type: value as MaintenanceType })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="service">Service</SelectItem>
                  <SelectItem value="repair">Repair</SelectItem>
                  <SelectItem value="inspection">Inspection</SelectItem>
                  <SelectItem value="load_test">Load Test</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-cost">Cost</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="edit-cost"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  className="pl-10"
                  value={formData.cost}
                  onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-notes">Notes</Label>
              <Textarea
                id="edit-notes"
                placeholder="Enter notes (optional)"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Maintenance Record</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this maintenance record? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
